import React from 'react';

import Axios, { AxiosError } from 'axios';

import API from 'utils/api';
import { isConflict } from 'utils/api/errors';
import { LibraryCategory, LibraryEntityType } from 'utils/api/modules/entities/entities.types';
import Logger from 'utils/logger';
import { useStore } from 'utils/hooks/store';

import { CategoryDraft } from './CategoryRow';

const nameErrorOf = (error: unknown): string | undefined => {
  const body = (error as AxiosError<{ name?: string[] | string }>).response?.data;
  const message = body && !Array.isArray(body) ? body.name : undefined;
  return Array.isArray(message) ? message[0] : message;
};

const toDrafts = (categories: LibraryCategory[]): CategoryDraft[] =>
  categories.map((category) => ({ id: category.key, name: category.name, category }));

type CategoryDrafts = {
  drafts: CategoryDraft[] | null;
  visible: CategoryDraft[];
  loadFailed: boolean;
  rowErrors: Record<string, string>;
  blockedId?: string;
  focusId?: string;
  saving: boolean;
  isDirty: boolean;
  hasEmptyExisting: boolean;
  load: () => Promise<void>;
  close: () => void;
  onAddRow: () => void;
  onChange: (id: string, name: string) => void;
  onDelete: (id: string) => void;
  onSave: () => Promise<void>;
};

const useCategoryDrafts = (entityType: LibraryEntityType, onClose: (changed: boolean) => void): CategoryDrafts => {
  const { notification } = useStore();

  const [drafts, setDrafts] = React.useState<CategoryDraft[] | null>(null);
  const [loadFailed, setLoadFailed] = React.useState(false);
  const [rowErrors, setRowErrors] = React.useState<Record<string, string>>({});
  const [blockedId, setBlockedId] = React.useState<string>();
  const [saving, setSaving] = React.useState(false);
  const [focusId, setFocusId] = React.useState<string>();
  const newCount = React.useRef(0);
  const changed = React.useRef(false);

  const fetchCategories = React.useCallback(
    async (cancelTokenSource?: ReturnType<typeof Axios.CancelToken.source>): Promise<LibraryCategory[] | null> => {
      try {
        const { data } = await API.entities.loadCategories(entityType, cancelTokenSource)();
        return data.results;
      } catch (e) {
        if (!Axios.isCancel(e)) {
          Logger.error('Failed to load categories', e);
          setLoadFailed(true);
        }
        return null;
      }
    },
    [entityType]
  );

  const load = React.useCallback(
    async (cancelTokenSource?: ReturnType<typeof Axios.CancelToken.source>): Promise<void> => {
      const results = await fetchCategories(cancelTokenSource);
      if (results) {
        setDrafts(toDrafts(results));
        setLoadFailed(false);
      }
    },
    [fetchCategories]
  );

  React.useEffect(() => {
    const cancelTokenSource = Axios.CancelToken.source();
    load(cancelTokenSource);
    return () => cancelTokenSource.cancel();
  }, [load]);

  const visible = (drafts ?? []).filter((draft) => !draft.removed);
  const hasEmptyExisting = visible.some((draft) => draft.category && !draft.name.trim());
  const isDirty = (drafts ?? []).some((draft) =>
    draft.category ? draft.removed || draft.name.trim() !== draft.category.name : draft.name.trim() !== ''
  );

  const onAddRow = (): void => {
    newCount.current += 1;
    const id = `new-${newCount.current}`;
    setDrafts((previous) => [...(previous ?? []), { id, name: '' }]);
    setFocusId(id);
  };

  const onChange = (id: string, name: string): void => {
    setDrafts((previous) => (previous ?? []).map((draft) => (draft.id === id ? { ...draft, name } : draft)));
    setRowErrors(({ [id]: _cleared, ...rest }) => rest);
  };

  const onDelete = (id: string): void => {
    setDrafts((previous) =>
      (previous ?? []).flatMap((draft) => {
        if (draft.id !== id) {
          return [draft];
        }
        return draft.category ? [{ ...draft, removed: true }] : [];
      })
    );
  };

  const applyDraft = async (draft: CategoryDraft): Promise<void> => {
    const name = draft.name.trim();
    if (draft.removed) {
      await API.entities.deleteCategory(entityType, draft.id)();
      changed.current = true;
    } else if (!draft.category) {
      if (name) {
        await API.entities.createCategory(entityType, name)();
        changed.current = true;
      }
    } else if (name !== draft.category.name) {
      await API.entities.renameCategory(entityType, draft.id, name)();
      changed.current = true;
    }
  };

  const close = (): void => onClose(changed.current);

  const onSave = async (): Promise<void> => {
    if (!drafts || saving) {
      return;
    }
    setSaving(true);
    setRowErrors({});
    setBlockedId(undefined);

    const failed = new Map<string, string>();
    let blocked: string | undefined;
    let unexpected = false;

    for (const draft of drafts) {
      try {
        await applyDraft(draft);
      } catch (e) {
        const message = nameErrorOf(e);
        if (!message && draft.removed && isConflict(e as AxiosError)) {
          blocked = draft.id;
        } else {
          unexpected = true;
        }
        failed.set(draft.id, message ?? '');
      }
    }

    if (failed.size === 0) {
      setSaving(false);
      close();
      return;
    }

    if (unexpected) {
      notification.enqueueErrorSnackbar('The categories could not be saved. Something went wrong.');
    }

    const fresh = await fetchCategories();
    if (fresh) {
      const typed = new Map(drafts.map((draft) => [draft.id, draft]));
      const rebuilt = toDrafts(fresh).map((draft) =>
        failed.has(draft.id) && !typed.get(draft.id)?.removed
          ? { ...draft, name: typed.get(draft.id)?.name ?? draft.name }
          : draft
      );
      const unsavedNew = drafts.filter((draft) => !draft.category && failed.has(draft.id));
      setDrafts([...rebuilt, ...unsavedNew]);
    }
    setRowErrors(Object.fromEntries([...failed].filter(([, message]) => message)));
    setBlockedId(blocked);
    setSaving(false);
  };

  return {
    drafts,
    visible,
    loadFailed,
    rowErrors,
    blockedId,
    focusId,
    saving,
    isDirty,
    hasEmptyExisting,
    load,
    close,
    onAddRow,
    onChange,
    onDelete,
    onSave,
  };
};

export default useCategoryDrafts;
